import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import type { ProjectMaterial, CreateProjectMaterial, UpdateProjectMaterial } from '@estimator/shared';

export function useProjectMaterials(projectId: string | undefined) {
  return useQuery({
    queryKey: ['project-materials', projectId],
    queryFn: async () => {
      const { data } = await apiClient.get<ProjectMaterial[]>('/project-materials', {
        params: { project_id: projectId },
      });
      return data;
    },
    enabled: !!projectId,
  });
}

export function useCreateProjectMaterial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateProjectMaterial) => {
      const { data } = await apiClient.post<ProjectMaterial>('/project-materials', body);
      return data;
    },
    onSuccess: (_data, vars) =>
      queryClient.invalidateQueries({ queryKey: ['project-materials', vars.project_id] }),
  });
}

export function useUpdateProjectMaterial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateProjectMaterial & { id: string }) => {
      const { data } = await apiClient.patch<ProjectMaterial>(`/project-materials/${id}`, body);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['project-materials'] }),
  });
}

export function useDeleteProjectMaterial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/project-materials/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['project-materials'] }),
  });
}
